// The policy texts for /terms, /privacy and /refunds, in English or Spanish.
//
// English is bundled with the page. Spanish loads on demand, all three texts in
// one "legal-es" chunk, so English readers never download it. If the Spanish
// text is missing or its chunk fails to load (offline, a deploy in between),
// the English text is shown instead, flagged englishOnly so the page can say so.
import { TERMS_MD } from "./terms";
import { PRIVACY_MD } from "./privacy";
import { REFUND_MD } from "./refund";

export const LEGAL_DOCS = ["terms", "privacy", "refunds"];
export const DOC_LANGS = ["en", "es"];

const ENGLISH = { terms: TERMS_MD, privacy: PRIVACY_MD, refunds: REFUND_MD };

const SPANISH = {
  terms: () => import(/* webpackChunkName: "legal-es" */ "./terms.es").then((m) => m.TERMS_ES_MD),
  privacy: () => import(/* webpackChunkName: "legal-es" */ "./privacy.es").then((m) => m.PRIVACY_ES_MD),
  refunds: () => import(/* webpackChunkName: "legal-es" */ "./refund.es").then((m) => m.REFUND_ES_MD),
};

const docKey = (doc) => (LEGAL_DOCS.includes(doc) ? doc : "terms");

// The document language asked for in the address (?doc=es or ?doc=en), or null.
// It picks the document only; the site language (?lng=) is left alone.
export function docLangParam(search) {
  const value = new URLSearchParams(search || "").get("doc");
  const lang = value ? value.trim().toLowerCase() : "";
  return DOC_LANGS.includes(lang) ? lang : null;
}

// The English text, available at once.
export function englishLegalDoc(doc) {
  const key = docKey(doc);
  return { doc: key, lang: "en", markdown: ENGLISH[key], englishOnly: false };
}

// { doc, lang, markdown, englishOnly }. Never rejects.
export async function loadLegalDoc(doc, lang) {
  const key = docKey(doc);
  if (lang !== "es") return englishLegalDoc(key);
  try {
    const markdown = await SPANISH[key]();
    if (typeof markdown === "string" && markdown.trim()) {
      return { doc: key, lang: "es", markdown, englishOnly: false };
    }
  } catch (err) {
    // Fall through to English.
  }
  return { ...englishLegalDoc(key), englishOnly: true };
}

// A heading's id: its text without the section number, accents or punctuation.
// "## 16. Aviso de privacidad integral" -> "aviso-de-privacidad-integral",
// "### 8.1 How renewal works" -> "how-renewal-works". Leaving the number out
// keeps deep links working when sections are renumbered.
export function slugify(text) {
  const slug = String(text || "")
    .replace(/^\s*\d+(?:\.\d+)*\.?\s+/, "")
    .replace(/[*_`]/g, "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || "section";
}

// Map of line number (1-based, as markdown positions count them) to a unique id,
// for every "## " and "### " heading. A repeated heading gets "-2", "-3"...
export function headingIds(markdown) {
  const ids = new Map();
  const seen = new Map();
  let fenced = false;
  String(markdown || "").split("\n").forEach((line, index) => {
    if (/^\s*(```|~~~)/.test(line)) fenced = !fenced;
    const match = !fenced && /^(#{2,3})\s+(.+?)\s*#*\s*$/.exec(line);
    if (!match) return;
    const base = slugify(match[2]);
    const count = (seen.get(base) || 0) + 1;
    seen.set(base, count);
    ids.set(index + 1, count === 1 ? base : `${base}-${count}`);
  });
  return ids;
}
